import { format, parseISO } from "date-fns";
import { MapPin, Phone, Mail, CalendarClock, Camera } from "lucide-react";

function PhotoStrip({ photos }) {
  if (!photos?.length) return null;
  return (
    <div className="mt-2 flex gap-2 overflow-x-auto photo-strip pb-1">
      {photos.map((p) => (
        <img
          key={p.id}
          src={p.url}
          alt="photo"
          className="h-16 w-16 rounded-lg object-cover border border-slate-200 shrink-0"
        />
      ))}
    </div>
  );
}

export default function Step5Summary({ data }) {
  return (
    <div className="space-y-6" data-testid="step-5-summary">
      <div>
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Review your request</h2>
        <p className="mt-2 text-slate-600">Double-check everything below, then submit. You'll get a confirmation email straight away.</p>
      </div>

      {/* Contact */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="summary-contact">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#FF7F00]">Contact</p>
        <p className="mt-2 font-display font-bold text-lg text-slate-900">{data.full_name || "—"}</p>
        <div className="mt-2 space-y-1 text-sm text-slate-600">
          <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-slate-400" />{data.phone || "—"}</p>
          <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-slate-400" />{data.email || "—"}</p>
        </div>
      </div>

      {/* Address */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="summary-address">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#FF7F00]">Property</p>
        <p className="mt-2 flex items-start gap-2 text-slate-800">
          <MapPin className="h-4 w-4 mt-1 text-slate-400 shrink-0" />
          <span>
            {data.street_address}<br />
            {data.postcode} · {data.property_type}
          </span>
        </p>
        {data.room_photos?.length > 0 && (
          <div className="mt-3">
            <p className="text-xs text-slate-500 mb-1 flex items-center gap-1.5">
              <Camera className="h-3.5 w-3.5" /> {data.room_photos.length} room photo{data.room_photos.length > 1 ? "s" : ""}
            </p>
            <PhotoStrip photos={data.room_photos} />
          </div>
        )}
      </div>

      {/* Items */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="summary-items">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#FF7F00]">Items to collect</p>
        <ul className="mt-3 space-y-2">
          {data.items?.map((it, i) => (
            <li key={i} className="flex items-center justify-between gap-2" data-testid={`summary-item-${i}`}>
              <p className="font-medium text-slate-900">{it.name}</p>
              <span className="text-xs font-semibold bg-[#FFF3E6] text-[#B85800] rounded-full px-2.5 py-0.5">× {it.quantity}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Date */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="summary-datetime">
        <p className="text-xs font-semibold uppercase tracking-wider text-[#FF7F00]">Preferred date</p>
        <p className="mt-2 flex items-center gap-2 text-slate-800">
          <CalendarClock className="h-4 w-4 text-slate-400" />
          {data.preferred_date ? format(parseISO(data.preferred_date), "EEEE, d MMM yyyy") : "—"}
        </p>
        {data.access_notes && (
          <p className="mt-2 text-sm text-slate-600"><span className="font-medium text-slate-700">Access notes:</span> {data.access_notes}</p>
        )}
      </div>

      <p className="text-xs text-slate-500 text-center">
        By submitting, you agree to be contacted by Waites Clearances about this booking.
      </p>
    </div>
  );
}
